import type { CsvDelimiter, ImportValueFix } from '@ofmos/shared/csv';
import { holdsTheObject } from '@ofmos/shared/csv';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAppClient } from '../../data/ClientProvider';
import type { CreateExportInput, ImportConfigPatch, ImportRunView } from '../../data/client';
import { queryKeys } from '../../data/queryKeys';

/**
 * THE MODULE'S CACHE ENTRIES (INV-13). Every key is declared in `data/queryKeys.ts` like
 * every other key in the app.
 *
 * WHAT POLLS AND WHAT DOES NOT is the only decision here, and D29 already made it: the
 * progress of a run is "the counters on the run row, read by polling at 2 seconds while
 * `running` and not at all in a terminal status". So the poll is a FUNCTION OF THE DATA
 * rather than a flag a screen passes - a screen that had to remember to switch it off is
 * a screen that will poll a finished run forever, which is the failure the workflow
 * module's own run list avoided by keying its stale time to what changes.
 *
 * The LIST is not polled. Fifty rows re-read every two seconds to watch one of them is
 * paying for the wrong thing; the mutations that change it invalidate it instead.
 */

const POLL_MS = 2_000;

export function useImportRuns() {
  const client = useAppClient();
  return useQuery({
    queryKey: queryKeys.imports(),
    queryFn: () => client.csv.listImports(),
    staleTime: 10_000,
  });
}

export function useImportRun(runId: string | null) {
  const client = useAppClient();
  return useQuery({
    queryKey: queryKeys.importRun(runId ?? 'none'),
    queryFn: () => client.csv.getImport(runId as string),
    enabled: runId !== null,
    /** D29: 2 seconds while the object is held, nothing once the run is terminal. */
    refetchInterval: (query) =>
      query.state.data !== undefined && holdsTheObject(query.state.data.status) ? POLL_MS : false,
  });
}

/**
 * The mapping screen's read, and a SECOND entry for the same run on purpose (see the key).
 * It changes when the file or the data model does, which is never while a run is going.
 */
export function useImportWizard(runId: string | null) {
  const client = useAppClient();
  return useQuery({
    queryKey: queryKeys.importWizard(runId ?? 'none'),
    queryFn: () => client.csv.getImportWizard(runId as string),
    enabled: runId !== null,
    staleTime: 30_000,
  });
}

export function useColumnReview(runId: string, columnIndex: number | null) {
  const client = useAppClient();
  return useQuery({
    queryKey: queryKeys.importColumnValues(runId, columnIndex ?? -1),
    queryFn: () => client.csv.reviewColumn(runId, columnIndex as number),
    enabled: columnIndex !== null,
    staleTime: 15_000,
  });
}

/**
 * D44's three numbers, read fresh when the dialog opens and never cached across dialogs:
 * "somebody has edited it since" is a fact about right now, and a stale skip count is
 * exactly the number the operator would be surprised by afterwards.
 */
export function useRevertPreview(runId: string | null) {
  const client = useAppClient();
  return useQuery({
    queryKey: queryKeys.importRevertPreview(runId ?? 'none'),
    queryFn: () => client.csv.revertPreview(runId as string),
    enabled: runId !== null,
    staleTime: 0,
    gcTime: 0,
  });
}

/**
 * EVERY IMPORT MUTATION WRITES THE RUN AND INVALIDATES THE LIST, because every one of
 * these routes answers with the whole run row - so refetching it would be a second
 * request for an answer already in hand.
 */
function useRunMutation<TInput>(run: (input: TInput) => Promise<ImportRunView>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: run,
    onSuccess: (result) => {
      queryClient.setQueryData(queryKeys.importRun(result.id), result);
      void queryClient.invalidateQueries({ queryKey: queryKeys.imports() });
    },
  });
}

export function useCreateImport() {
  const client = useAppClient();
  return useRunMutation((objectMetadataId: string) => client.csv.createImport(objectMetadataId));
}

export function useUploadImportFile(runId: string) {
  const client = useAppClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (file: {
      readonly filename: string;
      readonly text: string;
      /** D2's override: absent means the server sniffs it and says what it found. */
      readonly delimiter?: CsvDelimiter;
    }) => client.csv.uploadImportFile(runId, file),
    onSuccess: (result) => {
      queryClient.setQueryData(queryKeys.importRun(runId), result.run);
      /** A new file is new headers, new samples and a new auto-match. */
      void queryClient.invalidateQueries({ queryKey: queryKeys.importWizard(runId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.imports() });
    },
  });
}

export function useUpdateImport(runId: string) {
  const client = useAppClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (patch: ImportConfigPatch) => client.csv.updateImport(runId, patch),
    onSuccess: (result) => {
      queryClient.setQueryData(queryKeys.importRun(runId), result);
      /**
       * The wizard read carries the auto-match VERDICTS, which are computed against the
       * stored mapping - so a re-map changes what the mapping screen should be showing.
       */
      void queryClient.invalidateQueries({ queryKey: queryKeys.importWizard(runId) });
    },
  });
}

export function useSetValueFixes(runId: string) {
  const client = useAppClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      readonly columnIndex: number;
      readonly fixes: readonly ImportValueFix[];
    }) => client.csv.setValueFixes(runId, input.columnIndex, input.fixes),
    onSuccess: () => {
      /** A review edit demotes `ready` to `draft` on the server (D21), so the row is stale. */
      void queryClient.invalidateQueries({ queryKey: queryKeys.importRun(runId) });
      /**
       * AND THE WIZARD ENTRY, which is the one the review screen actually reads its stored
       * fixes off. Without this the local drafts clear on save and the run underneath still
       * says nothing was fixed, so a value somebody had just corrected reappeared as
       * untouched - the save worked and the screen denied it.
       */
      void queryClient.invalidateQueries({ queryKey: queryKeys.importWizard(runId) });
    },
  });
}

export function usePreviewImport(runId: string) {
  const client = useAppClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => client.csv.previewImport(runId),
    onSuccess: (result) => {
      queryClient.setQueryData(queryKeys.importRun(runId), result.run);
    },
  });
}

export function useStartImport(runId: string) {
  const client = useAppClient();
  return useRunMutation(() => client.csv.startImport(runId));
}

export function useRetryImport() {
  const client = useAppClient();
  return useRunMutation((runId: string) => client.csv.retryImport(runId));
}

export function useCancelImport() {
  const client = useAppClient();
  return useRunMutation((runId: string) => client.csv.cancelImport(runId));
}

/** A revert produces a NEW run, so the list is invalidated rather than written. */
export function useRevertImport() {
  const client = useAppClient();
  return useRunMutation((runId: string) => client.csv.revertImport(runId));
}

export function useExportRuns() {
  const client = useAppClient();
  return useQuery({
    queryKey: queryKeys.exports(),
    queryFn: () => client.csv.listExports(),
    /**
     * SHORTER THAN THE IMPORT LIST, and for the reason the webhook delivery board carries
     * its own: an export finishes while nobody is looking at it, and the whole point of
     * this screen is the Download button that appears when it does.
     */
    staleTime: 5_000,
    refetchInterval: (query) =>
      query.state.data?.some((run) => run.status === 'queued' || run.status === 'running') === true
        ? POLL_MS
        : false,
  });
}

export function useCreateExport() {
  const client = useAppClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateExportInput) => client.csv.createExport(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.exports() });
    },
  });
}

export function useRetryExport() {
  const client = useAppClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (runId: string) => client.csv.retryExport(runId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.exports() });
    },
  });
}
